/**
 * lavlahAnalytics — lightweight dual-write analytics helper for the
 * Lavlah.mn post-launch MVP.
 *
 * - Writes to Supabase `mvs_analytics_events` via the existing track-event
 *   edge function (works for signed-out users).
 * - Mirrors to GA4 via window.gtag if a measurement ID is present in
 *   `window.__GA4_ID__` or `import.meta.env.VITE_GA4_ID`.
 *
 * Never throws. Fire-and-forget.
 */
import { trackAnalyticsEvent } from "@/hooks/useAnalyticsTracking";

/** Lavlah-specific MVP event names tracked across the app. */
export type LavlahEventName =
  | "page_view"
  | "map_open"
  | "search"
  | "search_no_result"
  | "filter_apply"
  | "near_me_click"
  | "directions_click"
  | "place_open"
  | "save_place"
  | "share_place"
  | "add_place_start"
  | "add_place_abandoned"
  | "add_place_submit"
  | "add_price_submit"
  | "report_place"
  | "auth_handoff_start"
  | "edge_action_error"
  | "ai_seed_decision"
  | "quality_issue_dismissed"
  | "talk_post_submit"
  | "deal_submit"
  | "quick_signup_success"
  | "judge_vote"
  | "ranking_open"
  | "donation_start"
  | "donation_success"
  | "donation_failure";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    __GA4_ID__?: string;
  }
}

function getGA4Id(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    window.__GA4_ID__ ||
    (import.meta as any)?.env?.VITE_GA4_ID ||
    undefined
  );
}

function sendToGA4(event: LavlahEventName, params?: Record<string, unknown>) {
  try {
    if (typeof window === "undefined") return;
    const id = getGA4Id();
    if (!id || typeof window.gtag !== "function") return;
    window.gtag("event", event, params ?? {});
  } catch {
    // never throw
  }
}

/**
 * Track a Lavlah MVP event. Dual-writes to Supabase analytics_events table
 * (via track-event edge function) and to GA4 if configured.
 */
export function trackLavlahEvent(
  event: LavlahEventName,
  properties?: Record<string, unknown>,
): void {
  // GA4 (synchronous-ish, fire and forget)
  sendToGA4(event, properties);

  // Supabase — pass the real Lavlah event name through to the edge function.
  // The track-event edge function accepts arbitrary event_type strings at
  // runtime; we keep `lavlah_event` in properties for backward compatibility
  // with existing dashboards that filter on it.
  void trackAnalyticsEvent(event as unknown as "landing_view", {
    properties: {
      lavlah_event: event,
      ...(properties ?? {}),
    },
  });
}
